import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * Echt · the country/currency dropdown's disclosure behaviour, and nothing else.
 *
 * WHY THIS EXISTS RATHER THAN STOCK. `assets/localization.js` already ships a
 * `DropdownLocalizationComponent` that toggles a panel, and it was the first
 * thing tried. It cannot drive our markup: `#updateWidth()` reads
 * `this.refs.localizationForm.offsetWidth` with no guard (localization.js:475),
 * so opening the panel throws unless the panel contains stock's
 * `<localization-form-component ref="localizationForm">` — which is stock's
 * searchable country list, ~6.4 KB of CSS and a search field the design does
 * not have. `focusSearchInput()` and `resetForm()` are optional-chained; that
 * one line is not.
 *
 * So this is the disclosure half only: toggle, outside click, Escape. Switching
 * market is the form's job, not ours — every option is a native submit, and the
 * control works with this file blocked.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button that opens the panel.
 * @property {HTMLElement} panel - The panel, the trigger's next sibling.
 * @extends {Component<Refs>}
 */
class EchtLocalizationComponent extends Component {
  requiredRefs = ['trigger', 'panel'];

  #controller = new AbortController();

  /** @type {MutationObserver | undefined} */
  #headerObserver;

  connectedCallback() {
    super.connectedCallback();
    // Start shut, always. A disconnect while the panel is open aborts the
    // document listeners, so an element that is moved rather than destroyed —
    // Section Rendering replacing the header, the theme editor re-rendering the
    // section — would come back visually open with nothing left listening for
    // the click or the Escape that should close it. Re-arming on connect is
    // cheaper and more obviously correct than trying to preserve the listeners
    // across the move, and a shut panel is the right resting state anyway.
    this.#reset();
    this.#watchHeader();
  }

  /**
   * Closes this panel when the header opens its own search surface or drawer,
   * so the two never sit open at once. `echt-header.js` mirrors both states
   * onto attributes of `<echt-header-component>` itself (`data-view` for the
   * drawer, `data-search` for search) rather than firing an event, so a
   * MutationObserver on those two attributes is the clean hook — no event to
   * miss, no coupling beyond the attribute contract documented at the top of
   * that file.
   */
  #watchHeader() {
    const header = this.closest('echt-header-component');
    if (!header) return;

    this.#headerObserver = new MutationObserver(() => {
      const searchOpen = header.dataset.search === 'open';
      const drawerOpen = (header.dataset.view ?? 'closed') !== 'closed';
      if ((searchOpen || drawerOpen) && this.open) this.close();
    });
    this.#headerObserver.observe(header, { attributes: true, attributeFilter: ['data-view', 'data-search'] });
  }

  #reset() {
    this.refs.panel.hidden = true;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  }

  get open() {
    return !this.refs.panel.hidden;
  }

  toggle() {
    this.open ? this.close() : this.#open();
  }

  #open() {
    this.refs.panel.hidden = false;
    this.refs.trigger.setAttribute('aria-expanded', 'true');

    const { signal } = this.#controller;
    // Listened for on the document so a click anywhere dismisses the panel.
    // The header (`assets/echt-header.js`) also listens for `keydown` on
    // `document`, registered at its own `connectedCallback` — earlier than
    // this component's, since the header sits above it and connects first —
    // so its handler runs before this one on every Escape press regardless of
    // which listener uses capture or `stopPropagation`: those only stop a
    // *later* phase or sibling listeners in a *later* registration order, not
    // ones already ahead of it. This listener does not try to beat the header
    // to the key; it only closes this panel and returns focus to the trigger,
    // which is correct whether or not the header also reacted to the same
    // press.
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
  }

  close = () => {
    if (!this.open) return;
    this.#reset();
    this.#controller.abort();
    this.#controller = new AbortController();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#headerObserver?.disconnect();
    this.#headerObserver = undefined;
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (isClickedOutside(event, this)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.close();
    // Focus goes back to what opened the panel, per the WAI-ARIA disclosure
    // pattern — otherwise Escape strands the shopper at the top of the document.
    this.refs.trigger.focus();
  };
}

if (!customElements.get('echt-localization-component')) {
  customElements.define('echt-localization-component', EchtLocalizationComponent);
}
